import type {
  ComputedJavaScriptVariableState,
  SetVariableArrayTypePayload,
} from '@unifyapps/defs/types/javascriptVariable';
import _isUndefined from 'lodash/isUndefined';
import _slice from 'lodash/slice';
import isEqual from 'react-fast-compare';
import { EMPTY_ARRAY } from '../../../../../consts/empty';

/**
 * Any change here should be reflected in the METHODS_DOCUMENTATION.md file
 */
export function getNextPageVariableStateForArray({
  variableState,
  operationDetails,
  onError,
}: {
  variableState: ComputedJavaScriptVariableState<unknown>;
  operationDetails: {
    operation?: SetVariableArrayTypePayload['operationDetails']['operation'];
    value?: unknown;
    index?: number;
  };
  onError?: (message: string) => void;
}) {
  // a bad payload is logged to the dev console and surfaced to Pulse (via onError) as an error,
  // then the variable is left unchanged. Only the invalid-payload branches call this.
  const reportPayloadError = (message: string) => {
    console.error(message);
    onError?.(message);
  };
  switch (operationDetails.operation) {
    case 'SET': {
      // SET allows undefined to reset the variable, or an array to set it
      if (!_isUndefined(operationDetails.value) && !Array.isArray(operationDetails.value)) {
        reportPayloadError('operationDetails.value should be array or undefined for set operation');
        return variableState;
      }
      return {
        ...variableState,
        value: operationDetails.value,
      };
    }
    case 'APPEND': {
      // validate current state is array or undefined (for initialization)
      if (!_isUndefined(variableState.value) && !Array.isArray(variableState.value)) {
        reportPayloadError('variableState.value should be array or undefined for append operation');
        return variableState;
      }
      // validate input value is an array
      if (_isUndefined(operationDetails.value) || !Array.isArray(operationDetails.value)) {
        reportPayloadError(
          'operationDetails.value is required and should be array for append operation',
        );
        return variableState;
      }
      return {
        ...variableState,
        value: [
          ...((variableState.value as unknown[] | undefined) ?? EMPTY_ARRAY),
          ...(operationDetails.value as unknown[]),
        ],
      };
    }
    case 'PREPEND': {
      // validate current state is array or undefined (for initialization)
      if (!_isUndefined(variableState.value) && !Array.isArray(variableState.value)) {
        reportPayloadError('variableState.value should be array or undefined for prepend operation');
        return variableState;
      }
      // validate input value is an array
      if (_isUndefined(operationDetails.value) || !Array.isArray(operationDetails.value)) {
        reportPayloadError(
          'operationDetails.value is required and should be array for prepend operation',
        );
        return variableState;
      }

      return {
        ...variableState,
        value: [
          ...(operationDetails.value as unknown[]),
          ...((variableState.value as unknown[] | undefined) ?? EMPTY_ARRAY),
        ],
      };
    }
    case 'POP': {
      // validate current state is array or undefined (for initialization)
      if (!_isUndefined(variableState.value) && !Array.isArray(variableState.value)) {
        reportPayloadError('variableState.value should be array or undefined for pop operation');
        return variableState;
      }
      const array = (variableState.value as unknown[] | undefined) ?? EMPTY_ARRAY;
      if (array.length === 0) {
        return variableState;
      }
      return {
        ...variableState,
        value: _slice(array, 0, -1),
      };
    }
    case 'SHIFT': {
      // validate current state is array or undefined (for initialization)
      if (!_isUndefined(variableState.value) && !Array.isArray(variableState.value)) {
        reportPayloadError('variableState.value should be array or undefined for shift operation');
        return variableState;
      }
      const array = (variableState.value as unknown[] | undefined) ?? EMPTY_ARRAY;
      if (array.length === 0) {
        return variableState;
      }
      return {
        ...variableState,
        value: _slice(array, 1),
      };
    }
    case 'REMOVE_AT': {
      // validate current state is array or undefined (for initialization)
      if (!_isUndefined(variableState.value) && !Array.isArray(variableState.value)) {
        reportPayloadError('variableState.value should be array or undefined for remove_at operation');
        return variableState;
      }
      // validate index is provided and is a number
      if (_isUndefined(operationDetails.index) || typeof operationDetails.index !== 'number') {
        reportPayloadError('Index is required and should be a number for remove_at operation');
        return variableState;
      }
      const array = (variableState.value as unknown[] | undefined) ?? EMPTY_ARRAY;
      const index = operationDetails.index;
      if (index < 0 || index >= array.length) {
        reportPayloadError('Index out of bounds for remove_at operation');
        return variableState;
      }
      return {
        ...variableState,
        value: [...array.slice(0, index), ...array.slice(index + 1)],
      };
    }
    case 'REMOVE_BY_VALUE': {
      // validate current state is array or undefined (for initialization)
      if (!_isUndefined(variableState.value) && !Array.isArray(variableState.value)) {
        reportPayloadError(
          'variableState.value should be array or undefined for remove_by_value operation',
        );
        return variableState;
      }
      // validate value is provided
      if (_isUndefined(operationDetails.value)) {
        reportPayloadError('operationDetails.value is required for remove_by_value operation');
        return variableState;
      }
      const array = (variableState.value as unknown[] | undefined) ?? EMPTY_ARRAY;
      return {
        ...variableState,
        value: array.filter((value) => !isEqual(value, operationDetails.value)),
      };
    }
    default:
      return variableState;
  }
}
